import { useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { Plus, LogOut, Edit3, Star, Trash2 } from "lucide-react";
import { api, resolveImg } from "../../lib/api";
import { auth } from "../../lib/auth";

const CATS = [
  { slug: "musica", name: "Música" },
  { slug: "eventos", name: "Eventos" },
  { slug: "viajes", name: "Viajes" },
  { slug: "retratos", name: "Retratos" },
];

export default function AdminDashboard() {
  const [projects, setProjects] = useState([]);
  const [loading, setLoading] = useState(true);
  const [activeCat, setActiveCat] = useState("all");
  const navigate = useNavigate();

  const load = async () => {
    setLoading(true);
    const r = await api.get("/projects");
    setProjects(r.data);
    setLoading(false);
  };
  useEffect(() => { load(); }, []);

  const filtered = activeCat === "all" ? projects : projects.filter((p) => p.category === activeCat);

  const remove = async (slug) => {
    if (!confirm(`¿Eliminar el proyecto "${slug}"? Esta acción no se puede deshacer.`)) return;
    await api.delete(`/admin/projects/${slug}`);
    await load();
  };
  const toggleFeatured = async (p) => {
    await api.put(`/admin/projects/${p.slug}`, { featured: !p.featured });
    await load();
  };

  const logout = () => { auth.logout(); navigate("/admin/login", { replace: true }); };

  return (
    <div data-testid="admin-dashboard" className="min-h-screen bg-black text-white">
      <header className="border-b border-white/10">
        <div className="max-w-[1500px] mx-auto px-6 sm:px-12 py-6 flex items-center justify-between">
          <Link to="/admin" className="flex items-baseline gap-2">
            <span className="font-mono text-[10px] tracking-[0.4em] text-[var(--magenta)]">CONTROL ROOM</span>
            <span className="font-display uppercase tracking-[0.04em] text-2xl">EUPEGUI</span>
          </Link>
          <div className="flex items-center gap-4">
            <Link to="/" className="text-[11px] tracking-[0.3em] uppercase font-display text-white/60 hover:text-white">
              Ver sitio →
            </Link>
            <button onClick={logout} data-testid="admin-logout"
              className="inline-flex items-center gap-2 text-[11px] tracking-[0.3em] uppercase font-display text-white/60 hover:text-[var(--magenta)]">
              <LogOut size={14} /> Salir
            </button>
          </div>
        </div>
      </header>

      <main className="max-w-[1500px] mx-auto px-6 sm:px-12 py-10">
        <div className="flex items-end justify-between mb-8 gap-4 flex-wrap">
          <div>
            <p className="font-mono text-[10px] tracking-[0.4em] text-white/40">PROYECTOS</p>
            <h1 className="font-display uppercase tracking-tighter text-4xl sm:text-5xl mt-2">
              Archivo · {projects.length} series
            </h1>
          </div>
          <Link to="/admin/projects/new" data-testid="admin-new-project"
            className="inline-flex items-center gap-2 border border-[var(--magenta)] text-[var(--magenta)] px-5 py-3 text-[11px] tracking-[0.3em] uppercase font-display hover:bg-[var(--magenta)] hover:text-white transition">
            <Plus size={14} /> Nuevo proyecto
          </Link>
        </div>

        <div className="flex gap-2 mb-6 flex-wrap">
          {[{ slug: "all", name: "Todos" }, ...CATS].map((c) => (
            <button
              key={c.slug} onClick={() => setActiveCat(c.slug)}
              className={`px-4 py-2 text-[11px] tracking-[0.3em] uppercase font-display border transition ${
                activeCat === c.slug ? "border-white text-white" : "border-white/15 text-white/50 hover:text-white"
              }`}
            >{c.name}</button>
          ))}
        </div>

        {loading ? (
          <p className="text-white/40 font-mono text-xs">CARGANDO…</p>
        ) : (
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6">
            {filtered.map((p) => (
              <article key={p.slug} data-testid={`admin-project-${p.slug}`}
                className="border border-white/10 hover:border-white/30 transition group">
                <Link to={`/admin/projects/${p.slug}`} className="block aspect-[3/2] overflow-hidden bg-[var(--surface)]">
                  {p.cover && <img src={resolveImg(p.cover)} alt={p.title} className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-700" />}
                </Link>
                <div className="p-4 space-y-3">
                  <div className="flex items-start justify-between gap-2">
                    <div>
                      <p className="font-display uppercase tracking-tight text-lg leading-tight">{p.title}</p>
                      <p className="font-mono text-[10px] tracking-[0.3em] text-white/40 mt-1">
                        {p.category.toUpperCase()} · {p.images?.length || 0} fotos · orden {p.order}
                      </p>
                    </div>
                    <button onClick={() => toggleFeatured(p)}
                      className={`p-2 ${p.featured ? "text-[var(--tungsten)]" : "text-white/30 hover:text-white"}`}
                      title={p.featured ? "Destacado" : "Destacar"}>
                      <Star size={16} fill={p.featured ? "currentColor" : "none"} />
                    </button>
                  </div>
                  <div className="flex items-center gap-2">
                    <Link to={`/admin/projects/${p.slug}`} data-testid={`admin-edit-${p.slug}`}
                      className="flex-1 inline-flex items-center justify-center gap-2 border border-white/20 hover:border-white px-3 py-2 text-[10px] tracking-[0.3em] uppercase font-display">
                      <Edit3 size={12} /> Editar
                    </Link>
                    <button onClick={() => remove(p.slug)} data-testid={`admin-delete-${p.slug}`}
                      className="inline-flex items-center justify-center gap-2 border border-white/10 hover:border-[var(--magenta)] hover:text-[var(--magenta)] px-3 py-2 text-[10px] tracking-[0.3em] uppercase font-display">
                      <Trash2 size={12} />
                    </button>
                  </div>
                </div>
              </article>
            ))}
          </div>
        )}
      </main>
    </div>
  );
}
